import React from 'react';
import styled from 'styled-components';
import { Calendar, Clock, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const CardContainer = styled.article`
  background: var(--white);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  cursor: pointer;
  position: relative;
  border: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: transparent;

    .card-image img {
      transform: scale(1.08);
    }

    .arrow-icon {
      transform: translate(4px, -4px);
      opacity: 1;
    }

    .card-title {
      color: var(--primary-blue);
    }
  }
`;

const CardLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  height: 100%;
`;

const CardImage = styled.div`
  width: 100%;
  height: 200px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 38, 100, 0.7), transparent);
  }
`;

const CardCategory = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: var(--accent-gold);
  color: var(--primary-blue-dark);
  padding: 0.4rem 1rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 2;
  box-shadow: var(--shadow-sm);
`;

const ArrowIcon = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: var(--white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-blue);
  z-index: 2;
  opacity: 0;
  transition: var(--transition);
  box-shadow: var(--shadow-md);
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.2rem;
  color: var(--text-dark);
  margin-bottom: 0.75rem;
  line-height: 1.4;
  font-weight: 700;
  transition: var(--transition);
`;

const CardExcerpt = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  line-height: 1.6;
  margin-bottom: 1.25rem;
  flex: 1;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid var(--border-light);
  font-size: 0.85rem;
  color: var(--text-light);
  font-weight: 500;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  
  svg {
    color: var(--primary-blue);
    opacity: 0.6;
  }
`;

const NewsCard = ({ article }) => {
  const detailPath = article?.id ? `/actualites/${article.id}` : '/actualites';

  return (
    <CardLink to={detailPath} aria-label={`Lire l'article: ${article.title || 'Publication'}`}>
      <CardContainer>
        <CardImage className="card-image">
          <img src={article.image} alt={article.title} />
          <CardCategory>{article.category}</CardCategory>
          <ArrowIcon className="arrow-icon">
            <ArrowUpRight size={20} />
          </ArrowIcon>
        </CardImage>
        
        <CardContent>
          <CardTitle className="card-title">{article.title}</CardTitle>
          <CardExcerpt>{article.excerpt}</CardExcerpt>
          
          <CardMeta>
            <MetaItem>
              <Calendar size={16} />
              {article.date}
            </MetaItem>
            <MetaItem>
              <Clock size={16} />
              {article.readTime}
            </MetaItem>
          </CardMeta>
        </CardContent>
      </CardContainer>
    </CardLink>
  );
};

export default NewsCard;
